
import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { SupervisorMessage, AgentStatusUpdate, AgentRefreshRequest } from '../models/supervisor-communication';
import { StateEventMessage } from '../connectivity';

@Injectable({
  providedIn: 'root'
})
export class SupervisorCommunicationService {
  private messageSubject = new Subject<SupervisorMessage>();
  private agentRefreshSubject = new Subject<any>();
  private agentStatusUpdateSubject = new Subject<any>();

  constructor() { }

  /**
   * Observable for all supervisor messages
   */
  get messages$(): Observable<SupervisorMessage> {
    return this.messageSubject.asObservable();
  }

  /**
   * Observable specifically for agent refresh events
   */
  get agentRefresh$(): Observable<any> {
    return this.agentRefreshSubject.asObservable();
  }

  /**
   * Observable specifically for agent status updates
   */
  get agentStatusUpdate$(): Observable<any> {
    return this.agentStatusUpdateSubject.asObservable();
  }

  /**
   * Send a message to child components (like AgentsSupervisorComponent)
   */
  sendMessage(message: SupervisorMessage): void {

    // Add timestamp if not provided
    if (!message.timestamp) {
      message.timestamp = new Date();
    }

    this.messageSubject.next(message);

    // Also emit to specific subjects based on message type
    switch (message.type) {
      case 'agent_refresh':
      case 'agents_list_update':
        this.agentRefreshSubject.next(message.payload);
        break;
      case 'agent_status_update':
        this.agentStatusUpdateSubject.next(message.payload);
        break;
    }
  }

  refreshStateAgent(user_update: StateEventMessage): void {
    const agentId = (user_update as any).agent_id ?? user_update.id ?? '';

    // Convert StateEventMessage to the format expected by AgentsSupervisorComponent
    const formattedUpdate = {
      agent_id: agentId,
      new_status: user_update.status,
      last_update: user_update.lastUpdate,
      reason: 'WebSocket state update',
      // Include the full state event for comprehensive updates
      ...user_update
    };

    // Send to both subjects for broader compatibility
    this.agentStatusUpdateSubject.next(formattedUpdate);

    // Also send as a supervisor message for more structured handling
    this.sendMessage({
      type: 'agent_status_update',
      payload: formattedUpdate,
      timestamp: new Date()
    });
  }

  /**
   * Trigger a refresh of the agents list
   */
  refreshAgentsList(reason?: string): void {
    // console.log('🔄 SupervisorCommunicationService - Solicitando refresh de agentes:', reason);

    this.sendMessage({
      type: 'agent_refresh',
      payload: { reason: reason || 'Manual refresh requested' },
      timestamp: new Date()
    });
  }

  /**
   * Update specific agent status
   */
  updateAgentStatus(agentId: string, newStatus: string, additionalData?: any): void {

    this.sendMessage({
      type: 'agent_status_update',
      payload: {
        agent_id: agentId,
        new_status: newStatus,
        ...additionalData
      },
      timestamp: new Date()
    });
  }

  /**
   * Handle WebSocket message and route it appropriately
   */
  handleWebSocketMessage(message: any): void {

    // Check if message type is 'data' (as it always is)
    if (message.type !== 'data' || !message.payload) {
      // console.log('⚠️ Mensaje WebSocket no es de tipo data o no tiene payload:', message);
      return;
    }

    const event = message.payload.event;
    // console.log('🔍 Event type:', event);

    switch (event) {
      case 'user_state':
        // Handle agent status updates (this is the main case for agent data)
        this.handleUserStateUpdate(message.payload);
        break;

      case 'state_event':
        // Handle StateEventMessage directly
        console.log('🔄 SupervisorCommunicationService - Handling state_event:', message.payload);
        this.handleStateEventMessage(message.payload);
        break;

      case 'agent_state_changed':
        this.sendMessage({
          type: 'agent_state_changed',
          payload: message.payload,
          timestamp: new Date()
        });
        break;

      case 'agent_status_update':
        if (message.payload.agent_id && message.payload.new_status) {
          this.updateAgentStatus(
            message.payload.agent_id,
            message.payload.new_status,
            message.payload
          );
        }
        break;

      case 'agents_list_update':
      case 'supervisor_notification':
        this.refreshAgentsList(`WebSocket event: ${event}`);
        break;

      default:
        // Log unknown events but still try to process them
        // console.log('📨 Evento WebSocket no reconocido:', event);
        this.sendMessage({
          type: 'agent_refresh', // Default to agent refresh for unknown events
          payload: message.payload,
          timestamp: new Date()
        });
        break;
    }
  }
  /**
   * Handle StateEventMessage specifically - this is called from app.component.ts
   */
  private handleStateEventMessage(payload: any): void {
    console.log('📡 SupervisorCommunicationService - Processing StateEventMessage:', payload);

    const agentId = payload.agent_id || payload.id || payload._id;

    // Ensure we have required fields for an agent
    if (payload.role === 'agent' && agentId && payload.status) {
      const formattedUpdate = {
        agent_id: agentId,
        new_status: payload.status,
        last_update: payload.lastUpdate,
        reason: 'StateEvent WebSocket update',
        // Include the full payload for comprehensive updates
        ...payload
      };

      // Send to agent status update subject
      this.agentStatusUpdateSubject.next(formattedUpdate);

      // Also send as a structured supervisor message
      this.sendMessage({
        type: 'agent_status_update',
        payload: formattedUpdate,
        timestamp: new Date()
      });

      console.log('✅ StateEventMessage processed for agent:', payload.id);
    } else {
      console.log('ℹ️ StateEventMessage ignored - not an agent or missing required fields:', {
        role: payload.role,
        id: payload.id,
        status: payload.status
      });
    }
  }

  /**
   * Test method for debugging communication flow
   * Usage: supervisorCommunicationService.testStateUpdate()
   */
  testStateUpdate(testAgentId: string = 'test-agent-123'): void {
    console.log('🧪 SupervisorCommunicationService - Testing state update for agent:', testAgentId);

    const testStateEvent: any = {
      event_type: 'state_event',
      id: testAgentId,
      customId: null,
      email: 'test@example.com',
      username: 'Test Agent',
      version: '1.0.0',
      ip: '127.0.0.1',
      lastUpdate: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      role: 'agent',
      status: 'TEST_STATUS_FROM_SERVICE',
      campaigns: ['test-campaign'],
      timestamp: new Date().toISOString()
    };

    this.refreshStateAgent(testStateEvent);
  }

  /**
   * Handle user_state events which contain agent data updates
   * IMPROVED VERSION: Only updates individual agent, no unnecessary refresh
   */
  private handleUserStateUpdate(payload: any): void {
    // // console.log('👤 Procesando actualización de estado de usuario:', payload);

    // Extract agent information from the payload
    const agentId = payload.id || payload._id;
    const agentStatus = payload.status;
    const agentRole = payload.role;

    // Only process if this is an agent (not supervisor)
    if (agentRole === 'agent' && agentId && agentStatus) {

      // Send ONLY specific agent status update - no full refresh needed
      // The updateAgentStatus method will handle the case where agent is not found
      this.updateAgentStatus(agentId, agentStatus, {
        username: payload.username,
        email: payload.email,
        last_update: payload.last_update,
        reason: payload.reason,
        timestamp: payload.timestamp,
        // Include the full payload for comprehensive updates
        ...payload
      });

    } else {
      // console.log('ℹ️ Evento user_state ignorado - no es un agente o faltan datos:', { agentRole, agentId, agentStatus });
    }
  }

}
